import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { ManualWorklistDto } from '@his/shared';
import { get, qs } from '@/lib/api';
import { dateOnly } from '@/lib/utils';
import { DataTable } from '@/components/ui/layout';
import { Badge, Button, Card, CardContent, CardHeader, CardTitle } from '@/components/ui/primitives';

export function ManualPreAuthWorklist({ onOpenDraft }: { onOpenDraft: (preAuthId: string) => void }) {
  const navigate = useNavigate();
  const [cursor, setCursor] = useState<string | null>(null);
  const list = useQuery({
    queryKey: ['preauth-manual-worklist', cursor],
    queryFn: () => get<ManualWorklistDto>(`/insurance/pre-auths/manual-worklist${qs({ limit: 20, cursor: cursor ?? undefined })}`),
  });
  if (!list.isLoading && (list.data?.items.length ?? 0) === 0 && !cursor) return null;
  return (
    <Card className="mb-4">
      <CardHeader>
        <CardTitle className="text-sm">Manual pre-authorisation worklist</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        <p className="text-xs text-muted-foreground">
          Visits in departments that raise pre-authorisations by hand, and requests drafted when an appointment was booked. Open the visit to add what is missing, then send the request from here.
        </p>
        <DataTable
          dense
          rows={list.data?.items}
          loading={list.isLoading} refreshing={list.isFetching}
          error={list.error}
          rowKey={(row) => `${row.kind}-${row.encounterId ?? row.preAuthId}`}
          footer={
            cursor || list.data?.nextCursor ? (
              <div className="ms-auto flex gap-2">
                {cursor && (
                  <Button size="sm" variant="ghost" onClick={() => setCursor(null)}>
                    Newest
                  </Button>
                )}
                {list.data?.nextCursor && (
                  <Button size="sm" variant="outline" onClick={() => setCursor(list.data?.nextCursor ?? null)}>
                    Older
                  </Button>
                )}
              </div>
            ) : undefined
          }
          columns={[
            { header: 'When', sort: (row) => row.startedAt, cell: (row) => dateOnly(row.startedAt) },
            { header: 'Patient', sort: (row) => row.patientName, cell: (row) => <div><p>{row.patientName}</p><p className="text-xs text-muted-foreground">{row.mrn}</p></div> },
            { header: 'Department', sort: (row) => row.departmentName, cell: (row) => row.departmentName ?? '—' },
            { header: '', cell: (row) => (row.kind === 'draft' ? <Badge variant="warning">Drafted at booking</Badge> : <span className="text-xs">{row.orders} order{row.orders === 1 ? '' : 's'} · {row.requests} request{row.requests === 1 ? '' : 's'}</span>) },
            {
              header: '',
              align: 'right',
              cell: (row) =>
                row.kind === 'encounter' && row.encounterId ? (
                  <Button size="sm" variant="outline" onClick={() => navigate(`/consultations/${row.encounterId}`)}>
                    Open in EMR
                  </Button>
                ) : row.preAuthId ? (
                  <Button size="sm" variant="outline" onClick={() => onOpenDraft(row.preAuthId as string)}>
                    Complete the draft
                  </Button>
                ) : null,
            },
          ]}
        />
      </CardContent>
    </Card>
  );
}
